import React, { useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { FiCopy, FiCheck } from 'react-icons/fi';
import Shuffle from './Shuffle';
import axios from 'axios';
import './CAViewer.css';

// Use relative path for production, or env variable if set
const API_URL = process.env.REACT_APP_API_URL || '';
const TURNSTILE_SITE_KEY = '0x4AAAAAACRaXlugp7zucCMp';

function CAViewer() {
  const { uuid } = useParams();
  const [ca, setCa] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loadingTokenInfo, setLoadingTokenInfo] = useState(false);
  const [copied, setCopied] = useState(false);
  const turnstileRef = useRef();
  const isProcessingRef = useRef(false);

  const handleCaptchaSuccess = useCallback(async (token) => {
    // Prevent multiple simultaneous calls
    if (isProcessingRef.current) {
      return;
    }

    // Don't process if we already have CA
    if (ca) {
      return;
    }

    isProcessingRef.current = true;
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ca/${uuid}`, {
        token: token,
      });

      setCa(response.data.ca);
      setShowCaptcha(false);
      // Load token info after getting CA
      loadTokenInfo(response.data.ca);
    } catch (err) {
      setError(
        err.response?.data?.error || 'Failed to verify. Please try again.'
      );
      // Reset captcha on error
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    } finally {
      setLoading(false);
      isProcessingRef.current = false;
    }
  }, [uuid, ca]);

  const loadTokenInfo = useCallback(async (tokenCA) => {
    if (!tokenCA) return;

    setLoadingTokenInfo(true);
    try {
      const response = await axios.get(`${API_URL}/api/token/${tokenCA}`);
      setTokenInfo(response.data);
    } catch (err) {
      console.error('Failed to load token info:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingTokenInfo(false);
    }
  }, []);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(ca);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '+0.00%';
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const getPercentColor = (num) => {
    if (num === null || num === undefined) return '#666';
    return num >= 0 ? '#10b981' : '#ef4444';
  };

  return (
    <div className="viewer-container">
      <div className="card">
        <Shuffle
          text="CopyCA"
          shuffleDirection="right"
          duration={0.35}
          animationMode="evenodd"
          shuffleTimes={1}
          ease="power3.out"
          stagger={0.03}
          threshold={0.1}
          triggerOnce={true}
          triggerOnHover
          respectReducedMotion={true}
          loop={true}
          loopDelay={3}
          tag="h1"
          className="viewer-logo"
        />
        <p className="subtitle">Solana Contract Address</p>

        {showCaptcha && !ca && !loading && (
          <div className="captcha-section">
            <p className="captcha-label">Please verify you're human:</p>
            <div className="turnstile-wrapper">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={handleCaptchaSuccess}
                onError={() => {
                  setError('Captcha verification failed. Please try again.');
                  isProcessingRef.current = false;
                }}
                onExpire={() => {
                  if (turnstileRef.current) {
                    turnstileRef.current.reset();
                  }
                  isProcessingRef.current = false;
                }}
              />
            </div>
          </div>
        )}

        {loading && <div className="loading">Verifying...</div>}

        {error && <div className="error">{error}</div>}

        {ca && (
          <>
            <div className="ca-result">
              <p className="ca-label">Contract Address:</p>
              <div className="ca-box">
                <code className="ca-address">{ca}</code>
                <button 
                  onClick={copyToClipboard} 
                  className={`copy-button ${copied ? 'copied' : ''}`}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
            </div>

            {loadingTokenInfo && (
              <div className="token-info-loading">Loading token information...</div>
            )}

            {tokenInfo && (
              <div className="token-info">
                {(tokenInfo.logo_url || tokenInfo.name || tokenInfo.symbol) && (
                  <div className="token-header">
                    {tokenInfo.logo_url && (
                      <div className="token-logo-wrapper">
                        <img
                          src={tokenInfo.logo_url}
                          alt={tokenInfo.name || tokenInfo.symbol || 'Token logo'}
                          className="token-logo"
                        />
                      </div>
                    )}
                    {(tokenInfo.name || tokenInfo.symbol) && (
                      <div className="token-title">
                        {tokenInfo.name && (
                          <h2 className="token-name">{tokenInfo.name}</h2>
                        )}
                        {tokenInfo.symbol && (
                          <span className="token-symbol">({tokenInfo.symbol})</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(tokenInfo.market_cap > 0) && (
                  <div className="token-stats">
                    {tokenInfo.market_cap > 0 && (
                      <div className="stat-item">
                        <span className="stat-label">Market Cap</span>
                        <span className="stat-value">{formatNumber(tokenInfo.market_cap)}</span>
                      </div>
                    )}
                  </div>
                )}

                {tokenInfo.price_change && (
                  <div className="price-changes">
                    <div className="price-changes-grid">
                      <div className="price-change-item">
                        <span className="price-change-label">5m</span>
                        <span
                          className="price-change-value"
                          style={{ color: getPercentColor(tokenInfo.price_change['5m']) }}
                        >
                          {formatPercent(tokenInfo.price_change['5m'])}
                        </span>
                      </div>
                      <div className="price-change-item">
                        <span className="price-change-label">1h</span>
                        <span
                          className="price-change-value"
                          style={{ color: getPercentColor(tokenInfo.price_change['1h']) }}
                        >
                          {formatPercent(tokenInfo.price_change['1h'])}
                        </span>
                      </div>
                      <div className="price-change-item">
                        <span className="price-change-label">6h</span>
                        <span
                          className="price-change-value"
                          style={{ color: getPercentColor(tokenInfo.price_change['6h']) }}
                        >
                          {formatPercent(tokenInfo.price_change['6h'])}
                        </span>
                      </div>
                      <div className="price-change-item">
                        <span className="price-change-label">24h</span>
                        <span
                          className="price-change-value"
                          style={{ color: getPercentColor(tokenInfo.price_change['24h']) }}
                        >
                          {formatPercent(tokenInfo.price_change['24h'])}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {(tokenInfo.website_url || tokenInfo.twitter_url) && (
                  <div className="token-links">
                    {tokenInfo.website_url && (
                      <a
                        href={tokenInfo.website_url}
                        className="token-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {tokenInfo.website_label || 'Website'}
                      </a>
                    )}
                    {tokenInfo.twitter_url && (
                      <a
                        href={tokenInfo.twitter_url}
                        className="token-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Twitter
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="back-link">
          <a href="/">Create new link</a>
        </div>
      </div>
    </div>
  );
}

export default CAViewer;
